import * as React from 'react';
import { ContentTypeFilterAllOption } from '../../search/models/ContentTypeFilterValue';
import { DefaultContentTypeOrder } from '../../search/models/ContentTypeOrder';
import { FacetResult } from '../../search/models/FacetTypes';
import { MixedContentTypeSearchResult, SearchResultItem, SearchResultList } from '../../search/models/SearchResultTypes';
import { FilterAllOption } from '../../search/state/FilterState';
import { ListItem } from '@tableau/widgets';
import * as Layout from '../../common/styles/Layout';
import { classNames } from '../styles/Utils';
import { CommonStyles } from '../styles/CommonStyles';
import { formatTotalSearchResultCount } from './L10n';
import { Messages } from '../../../gen-src/Localize';
import { Location } from '../../common/utils/Location';
import * as queryString from 'query-string';
import { Bar } from '../../search/components/filters/quickFilter/Bar';
export interface ListItemWithDisplayText extends ListItem {
  displayText: string;
}

export namespace FilterHelpers {
  export function getQuickFilterListValuesByType(
    contentType: string,
    singleContentResults: SearchResultList<SearchResultItem> | undefined,
    mixedContentResults: MixedContentTypeSearchResult | undefined,
    getFacetsData: (searchResultList?: SearchResultList<SearchResultItem>) => Array<FacetResult> | undefined
  ): FacetResult[] {
    const filterValues = new Map();
    if (contentType !== ContentTypeFilterAllOption) {
      return getFacetsData(singleContentResults) || [];
    } else {
      DefaultContentTypeOrder.forEach((contentType) => {
        const content = mixedContentResults?.map[contentType];
        if (content) {
          const data = getFacetsData(content);
          if (data) {
            data.forEach((item: FacetResult) => {
              let foundItem = false;
              if (filterValues.has(item.value.id)) {
                filterValues.get(item.value.id).count += item.count;
                foundItem = true;
              }
              if (!foundItem) {
                filterValues.set(item.value.id, { count: item.count, value: { name: item.value.name, id: item.value.id } } as FacetResult);
              }
            });
          }
        }
      });
      const result: FacetResult[] = Array.from(filterValues.values());
      return result;
    }
  }

  export function getFilterValues(
    facets: FacetResult[],
    filterAllOptionMessage?: string,
    selectedFilterMissingFromFacet?: { id: string; value: string },
    useBar?: boolean,
    maxBarValue?: number
  ): ListItemWithDisplayText[] {
    const list: ListItemWithDisplayText[] = [];
    if (filterAllOptionMessage) {
      list.push({
        content: (
          <div className={Layout.flexH}>
            <div className={Layout.flexAuto}>{filterAllOptionMessage}</div>
          </div>
        ),
        value: FilterAllOption,
        displayText: '',
      });
    }
    facets.forEach((item) => {
      if (item.value.name && item.value.id) {
        const resultCount = formatTotalSearchResultCount(item.count, useBar);
        list.push(formattedFilterRow(resultCount, item.value.name, item.value.id, item.count, useBar, maxBarValue));
      }
    });
    if (selectedFilterMissingFromFacet?.value && selectedFilterMissingFromFacet?.id) {
      // needed for the Direct Link scenario, where an owner name does not exist in the facets list, but the associated filter id was
      // entered in the url and thus needs to be added to the filter list
      list.push(formattedFilterRow('0', selectedFilterMissingFromFacet.value, selectedFilterMissingFromFacet.id, 0, useBar, maxBarValue));
    }
    return list;
  }

  export function hasFacets(
    contentType: string,
    singleContentResults: SearchResultList<SearchResultItem> | undefined,
    mixedContentResults: MixedContentTypeSearchResult | undefined,
    getFacetsCount: (searchResultList?: SearchResultList<SearchResultItem>) => number | undefined
  ): boolean {
    if (contentType !== ContentTypeFilterAllOption) {
      return getFacetsCount(singleContentResults) ? true : false;
    } else {
      for (const type of DefaultContentTypeOrder) {
        const content = mixedContentResults?.map[type];
        if (content) {
          if (getFacetsCount(content)) {
            return true;
          }
        }
      }
      return false;
    }
  }

  export function getSelectedFilterName(facets: FacetResult[], filterSelected: string) {
    return facets.find((x) => x.value.id.toString() === filterSelected)?.value.name;
  }

  export function getOwnerIdFromUrl(): string | undefined {
    const query = Location.hash().split('?')[1];
    return query ? queryString.parse(query)['owner'] : undefined;
  }
}

function formattedFilterRow(resultCount: string, name: string, id: string, itemCount: number, useBar?: boolean, maxBarValue?: number) {
  const ariaLabel = Messages.FilterDropdownOption_ariaLabel({
    COUNT_LOC: resultCount,
    COUNT: itemCount === undefined ? 0 : itemCount,
    ITEM: name,
  });
  return {
    content: (
      // This aria-label will work for NVDA but not VoiceOver
      <div className={Layout.flexH} aria-label={ariaLabel} title={ariaLabel}>
        <div className={classNames(Layout.flexAuto, Layout.relative)}>
          <div className={classNames(Layout.absolute, Layout.fulfill)} style={{ ...CommonStyles.Layout.TextEllipsis, paddingRight: '5px' }}>
            {name}
          </div>
        </div>
        <div>{useBar ? <Bar min={0} max={maxBarValue || 0} value={itemCount} style={{ width: 50 }} /> : resultCount}</div>
      </div>
    ),
    value: id,
    displayText: name,
  };
}
